import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Store } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { submitForm } from '@/lib/submission';
import { toast } from 'sonner';

interface SuggestDealerDialogProps {
  modelId: string;
  modelName: string;
  brandName: string;
}

export function SuggestDealerDialog({ modelId, modelName, brandName }: SuggestDealerDialogProps) {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dealerName, setDealerName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [location, setLocation] = useState('');
  const [email, setEmail] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dealerName.trim() || !websiteUrl.trim()) return;

    setIsSubmitting(true);
    const url = websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`;
    const payload = {
      model_id: modelId,
      dealer_name: dealerName.trim(),
      website_url: url.trim(),
      location: location.trim() || null,
      submitter_email: email.trim() || null,
      status: 'pending' as const,
    };
    const res = await submitForm({
      formName: 'suggest_dealer',
      table: 'dealer_links',
      payload,
      email: payload.submitter_email,
      meta: { brand: brandName, model: modelName },
      perform: () => (supabase.from as unknown as (t: string) => { insert: (v: unknown) => Promise<{ error: unknown }> })('dealer_links').insert(payload) as unknown as Promise<{ data: null; error: null }>,
    });
    if (res.ok) {
      toast.success('Tak! Dit forslag er sendt til godkendelse.');
      setOpen(false);
      setDealerName('');
      setWebsiteUrl('');
      setLocation('');
      setEmail('');
    } else {
      toast.error(res.userMessage ?? 'Noget gik galt. Prøv igen.');
    }
    setIsSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Store className="w-4 h-4" />
          Foreslå forhandler
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Foreslå forhandler til {brandName} {modelName}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="dealerName">Forhandlernavn *</Label>
            <Input
              id="dealerName"
              value={dealerName}
              onChange={(e) => setDealerName(e.target.value)}
              placeholder="F.eks. Andersen Auto"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="websiteUrl">Hjemmeside *</Label>
            <Input
              id="websiteUrl"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://www.forhandler.dk"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="location">Lokation (valgfrit)</Label>
            <Input
              id="location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="F.eks. København"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Din e-mail (valgfrit)</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="din@email.dk"
            />
          </div>
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? 'Sender...' : 'Send forslag'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
